import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { bulbOutline, copyOutline, checkmarkOutline, arrowBackOutline, playOutline } from 'ionicons/icons';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import VideoPlayer from '../../components/VideoPlayer/VideoPlayer';
import VideoTimeline from '../../components/VideoTimeline/VideoTimeline';
import EventsSidebar from '../../components/EventsSidebar/EventsSidebar';
import PlaybackControls from '../../components/PlaybackControls/PlaybackControls';
import VideoCaptions from '../../components/VideoCaptions/VideoCaptions';
import TabSection from '../../components/atoms/TabSection/TabSection';
import TestExecutionReport from '../../components/TestExecutionReport/TestExecutionReport';
import '../../components/VideoPlayer/VideoPlayer.css';
import '../../components/VideoTimeline/VideoTimeline.css';
import '../../components/EventsSidebar/EventsSidebar.css';
import '../../components/PlaybackControls/PlaybackControls.css';
import '../../components/VideoCaptions/VideoCaptions.css';
import api from '../../services/api';
import './ExecutionDetailsPage.css';

const getEventDescription = (event) => {
  if (event.reasoning) return event.reasoning;
  if (event.description) return event.description;
  if (event.step) return event.step;
  
  const params = event.data?.params || {};
  switch (event.type) {
    case 'navigate':
      return `Navigate to ${params.url || 'page'}`;
    case 'click':
      return `Click "${params.element || 'element'}"`;
    case 'type':
      return `Type "${params.text || ''}" into ${params.element || 'field'}`;
    case 'scroll':
      return `Scroll ${params.direction || 'down'}`;
    case 'wait':
      return `Wait for ${params.condition || 'element'}`;
    case 'screenshot':
      return 'Take screenshot';
    default:
      return event.type;
  }
};

const ExecutionDetailsPage = ({ setExecutionName, setExecutionCollectionId }) => {
  const { executionId, projectId } = useParams();
  const videoPlayerRef = useRef(null);
  
  const [execution, setExecution] = useState(null);
  const [events, setEvents] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [activeEventIndex, setActiveEventIndex] = useState(null);
  const [generatedScript, setGeneratedScript] = useState(null);
  const [testInstructions, setTestInstructions] = useState(null);
  const [copied, setCopied] = useState(false);
  const [cinematicMode, setCinematicMode] = useState(() => {
    const saved = localStorage.getItem('cinematicMode');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    fetchExecutionDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId]);

  // Handle spacebar to toggle play/pause
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only handle spacebar if not typing in an input/textarea
      if (e.code === 'Space' && 
          e.target.tagName !== 'INPUT' && 
          e.target.tagName !== 'TEXTAREA' &&
          !e.target.isContentEditable) {
        e.preventDefault();
        
        // Directly click the video element to trigger its built-in toggle
        const videoElement = document.querySelector('.zibby-video-player');
        if (videoElement) {
          videoElement.click();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    if (events.length === 0) return;
    
    const currentMs = currentTime * 1000;
    let active = null;
    
    // Filter to only action events (same as EventsSidebar)
    const actionEvents = events.filter(e => e.type === 'action' || e.videoOffsetMs !== undefined);
    
    actionEvents.forEach((event, index) => {
      if (event.videoOffsetMs !== undefined && event.videoOffsetMs <= currentMs) {
        active = index;
      }
    });
    
    setActiveEventIndex(active);
  }, [currentTime, events]);

  const fetchExecutionDetails = async () => {
    try {
      const data = await api.get(`/executions/${executionId}`);
      setExecution(data);
      
      if (setExecutionName) {
        setExecutionName(data.name || data.testName || 'Execution Details');
      }
      
      if (setExecutionCollectionId && data.collectionId) {
        setExecutionCollectionId(data.collectionId);
      }

      if (data.urls?.events) {
        try {
          const eventsResponse = await fetch(data.urls.events);
          if (eventsResponse.ok) {
            const eventsData = await eventsResponse.json();
            setEvents(eventsData);
          }
        } catch (error) {
          console.error('Error fetching events:', error);
          setEvents([]);
        }
      } else {
        setEvents([]);
      }

      // Fetch generated script if available
      if (data.urls?.generatedTest) {
        try {
          const scriptResponse = await fetch(data.urls.generatedTest);
          if (scriptResponse.ok) {
            const scriptText = await scriptResponse.text();
            setGeneratedScript(scriptText);
          }
        } catch (error) {
          console.error('Error fetching generated script:', error);
          setGeneratedScript(null);
        }
      }

      // Fetch test instructions if available (original .txt file)
      if (data.urls?.testInstructions) {
        try {
          const instructionsResponse = await fetch(data.urls.testInstructions);
          if (instructionsResponse.ok) {
            const instructionsText = await instructionsResponse.text();
            setTestInstructions(instructionsText);
          }
        } catch (error) {
          console.error('Error fetching test instructions:', error);
          setTestInstructions(null);
        }
      }

      // Fetch test result (assertions data)
      if (data.urls?.result) {
        try {
          const resultResponse = await fetch(data.urls.result);
          if (resultResponse.ok) {
            const resultData = await resultResponse.json();
            setTestResult(resultData);
          }
        } catch (error) {
          console.error('Error fetching test result:', error);
          setTestResult(null);
        }
      }
    } catch (error) {
      console.error('Error fetching execution:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeUpdate = (time) => {
    setCurrentTime(time);
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (videoPlayerRef.current) {
      videoPlayerRef.current.setPlaybackRate(speed);
    }
  };

  const toggleCinematicMode = () => {
    const newMode = !cinematicMode;
    setCinematicMode(newMode);
    localStorage.setItem('cinematicMode', JSON.stringify(newMode));
  };

  const handleSeek = (time) => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.seekTo(time);
    }
  };

  const handleEventClick = (event, index) => {
    console.log('Event clicked:', event.type, event.videoOffsetMs, videoPlayerRef.current);
    // Update active event index to highlight the clicked event
    setActiveEventIndex(index);
    if (event.videoOffsetMs !== undefined && videoPlayerRef.current) {
      videoPlayerRef.current.seekTo(event.videoOffsetMs / 1000);
    }
  };

  const handleCopyScript = async () => {
    if (generatedScript) {
      try {
        await navigator.clipboard.writeText(generatedScript);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy script:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="execution-fullscreen">
        <div className="execution-main">
          <div className="video-section-fullscreen">
            <div className="video-skeleton">
              <div className="skeleton-shimmer" />
            </div>
          </div>
          <div className="events-sidebar-fullscreen">
            <div className="skeleton-header" />
            <div className="skeleton-event-item" />
            <div className="skeleton-event-item" />
            <div className="skeleton-event-item" />
          </div>
        </div>
      </div>
    );
  }

  if (!execution) {
    return (
      <NotFoundPage
        code="404"
        title="Execution not found"
        subtitle="This run doesn't exist, or it may have been deleted. Check the URL or browse the project's runs."
        primaryAction={{ label: 'Back to runs', to: `/projects/${projectId}/testruns`, icon: arrowBackOutline }}
        secondaryAction={{ label: 'All runs', to: `/projects/${projectId}/testruns`, icon: playOutline }}
      />
    );
  }

  const captions = events.map(event => ({
    text: getEventDescription(event),
    startTime: event.videoOffsetMs ? event.videoOffsetMs / 1000 : 0,
    endTime: event.videoOffsetMs ? (event.videoOffsetMs / 1000) + 5 : 5
  }));

  // Tab content
  const tabs = [
    {
      label: 'Summary',
      content: (
        <TestExecutionReport 
          execution={execution}
          events={events}
          testInstructions={testInstructions}
          testResult={testResult}
          onJumpToTime={handleSeek}
        />
      )
    },
    {
      label: 'Test Case',
      content: (
        <div className="overview-content">
          {testInstructions ? (
            <SyntaxHighlighter 
              language="text" 
              style={vscDarkPlus}
              customStyle={{
                margin: 0,
                padding: '20px',
                background: '#1e1e1e',
                borderRadius: '8px',
                fontSize: 'var(--test-case-font-size)',
                maxHeight: 'none',
                overflow: 'visible'
              }}
              codeTagProps={{
                style: {
                  lineHeight: 'var(--code-line-height)'
                }
              }}
              wrapLongLines={true}
              showLineNumbers={false}
            >
              {testInstructions}
            </SyntaxHighlighter>
          ) : execution.specContent ? (
            <SyntaxHighlighter 
              language="text" 
              style={vscDarkPlus}
              customStyle={{
                margin: 0,
                padding: '20px',
                background: '#1e1e1e',
                borderRadius: '8px',
                fontSize: 'var(--test-case-font-size)',
                maxHeight: 'none',
                overflow: 'visible'
              }}
              codeTagProps={{
                style: {
                  lineHeight: 'var(--code-line-height)'
                }
              }}
              wrapLongLines={true}
              showLineNumbers={false}
            >
              {execution.specContent}
            </SyntaxHighlighter>
          ) : (
            <p>No test case content available</p>
          )}
        </div>
      )
    },
    {
      label: 'Scripts',
      content: (
        <div className="scripts-content">
          {generatedScript ? (
            <div className="script-container">
              <button className="copy-script-button" onClick={handleCopyScript}>
                <IonIcon icon={copied ? checkmarkOutline : copyOutline} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <SyntaxHighlighter 
                language="javascript" 
                style={vscDarkPlus}
                customStyle={{
                  margin: 0,
                  padding: '20px',
                  paddingTop: '25px',
                  background: '#1e1e1e',
                  borderRadius: '8px',
                  fontSize: 'var(--code-font-size)',
                  maxHeight: 'none',
                  overflow: 'visible'
                }}
                codeTagProps={{
                  style: {
                    lineHeight: 'var(--code-line-height)'
                  }
                }}
                wrapLongLines={true}
                showLineNumbers={true}
              >
                {generatedScript}
              </SyntaxHighlighter>
            </div>
          ) : (
            <p>No generated script available</p>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="execution-fullscreen">
      <div className="execution-main">
        <div className="video-section-fullscreen">
          {execution.urls?.video ? (
            <>
              <div className="video-wrapper-with-timeline">
                <div className={`video-container ${cinematicMode ? 'cinematic-mode' : ''}`}>
                  <VideoPlayer
                    ref={videoPlayerRef}
                    videoSrc={execution.urls.video}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={setDuration}
                  />
                  <button 
                    className="cinematic-toggle"
                    onClick={toggleCinematicMode}
                    title={cinematicMode ? 'Disable cinematic mode' : 'Enable cinematic mode'}
                  >
                    <IonIcon icon={bulbOutline} />
                  </button>
                  {duration > 0 && (
                    <PlaybackControls
                      currentSpeed={playbackSpeed}
                      onSpeedChange={handleSpeedChange}
                    />
                  )}
                  <VideoCaptions
                    captions={captions}
                    currentTime={currentTime}
                  />
                </div>
                {duration > 0 && (
                  <VideoTimeline
                    currentTime={currentTime}
                    duration={duration}
                    events={events}
                    onSeek={handleSeek}
                    onMarkerClick={handleSeek}
                  />
                )}
              </div>

              <TabSection tabs={tabs} defaultTab={0} />
            </>
          ) : (
            <div className="no-video">No video available</div>
          )}
        </div>

        {events.length > 0 && (
          <div className="events-sidebar-fullscreen">
            <EventsSidebar
              events={events}
              activeEventIndex={activeEventIndex}
              onEventClick={handleEventClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ExecutionDetailsPage;
